/* Batch #14.143 — PublicBailSignPage : signature publique via magic link.
 *
 * URL : /sign/bail/:token
 * Pas d'authentification — le token est la clé.
 *
 * Affiche :
 *   - Statut du workflow (qui a signé, qui doit signer)
 *   - PDF du bail dans un iframe (preview avec signatures déjà collectées)
 *   - Canvas de signature (drag pour dessiner)
 *   - Bouton "Signer"
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/lib/toast";
import {
  FileText, CheckCircle2, Clock, Loader2, Trash2, Send, ShieldCheck,
} from "lucide-react";
import PdfFrame, { isMobileOrIOS } from "@/components/shared/PdfFrame";
import { API } from "@/api";


export default function PublicBailSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfUrlRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/bail-sign/${token}/info`);
      setInfo(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const loadPdf = async () => {
    try {
      const r = await axios.get(`${API}/public/bail-sign/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = url;
      setPdfUrl(url);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    loadPdf();
    return () => { if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Canvas drawing
  const setupCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  };

  useEffect(() => {
    setupCanvas(canvasRef.current);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const p = e.touches?.[0] ?? e;
    return { x: (p.clientX - rect.left) * (canvas.width / rect.width), y: (p.clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDraw = (e) => {
    e.preventDefault?.();
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault?.();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDraw = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submit = async () => {
    if (!accepted) {
      toast.error("Veuillez accepter les conditions");
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Validate that canvas has something drawn (sum of pixels > 0)
    const ctx = canvas.getContext("2d");
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let hasInk = false;
    for (let i = 3; i < imgData.data.length; i += 4) {
      if (imgData.data[i] > 0) { hasInk = true; break; }
    }
    if (!hasInk) {
      toast.error("Veuillez signer dans la zone");
      return;
    }
    const sigB64 = canvas.toDataURL("image/png");

    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/public/bail-sign/${token}/sign`,
        { signature_b64: sigB64, accept_terms: true }
      );
      // Batch #14.160 — Toast en try/catch séparé : si une extension navigateur
      // (Google Translate, DeepL, etc.) a perturbé le DOM, on n'echoue pas la
      // soumission. La signature EST enregistrée côté serveur.
      try {
        toast.success(
          r.data.completed
            ? "✓ Bail entièrement signé. Merci !"
            : `Votre signature est enregistrée. Prochain : ${r.data.next_signer?.name}`,
          { duration: 6000 }
        );
      } catch (_toastErr) { /* DOM extension interference — ignore */ }
      try { await load(); } catch (_e) { /* re-render hiccup — soft ignore */ }
      try { await loadPdf(); } catch (_e) { /* idem */ }
    } catch (e) {
      // Batch #14.160 — Distingue les vraies erreurs réseau/API des erreurs DOM.
      // Si la réponse est OK mais une exception est levée APRÈS (ex: NotFoundError
      // d'une extension), on n'affiche pas d'erreur trompeuse à l'utilisateur.
      const isDomErr = e?.name === "NotFoundError" || /NotFoundError|removeChild|appendChild/i.test(String(e?.message || ""));
      if (isDomErr) {
        // eslint-disable-next-line no-console
        console.warn("[bail-sign] DOM interference ignored:", e);
        toast.success("Signature enregistrée. Veuillez désactiver les extensions de traduction de votre navigateur si le problème persiste.", { duration: 8000 });
        try { await load(); } catch (_e) { /* noop */ }
      } else {
        toast.error(e.response?.data?.detail || "Erreur d'envoi");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Batch #14.160 — Désactive Google Translate sur cette page publique
  // (Google Translate provoque des NotFoundError React lors des re-render
  // car il remplace les text nodes que React tente ensuite de removeChild).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "google";
    meta.content = "notranslate";
    document.head.appendChild(meta);
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
    return () => {
      try { document.head.removeChild(meta); } catch { /* noop */ }
      document.documentElement.removeAttribute("translate");
      document.documentElement.classList.remove("notranslate");
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-rose-50 p-8">
        <div className="bg-white p-8 rounded-xl shadow text-center max-w-md">
          <h1 className="text-xl font-bold text-rose-700 mb-2">Lien invalide</h1>
          <p className="text-sm text-gray-600">Ce lien de signature n&apos;est plus valide ou a expiré.</p>
        </div>
      </div>
    );
  }

  const alreadySigned = info.signer.status === "signed";
  const completed = info.workflow_status === "completed";
  const notMyTurn = !info.is_my_turn && !alreadySigned;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 notranslate" translate="no" data-testid="public-bail-sign-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-indigo-600" />
          <div>
            <div className="text-sm font-bold text-gray-900">DG Comptabilité — Signature électronique</div>
            <div className="text-xs text-gray-500">{info.title}</div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left : info + progress + signature */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              <h2 className="text-base font-semibold">Bonjour {info.signer.name}</h2>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Vous êtes invité(e) à signer ce bail en tant que <strong>{info.signer.role === "tenant" ? "locataire" : "propriétaire"} #{info.signer.position}</strong>.
            </p>
            <div className="space-y-1.5">
              {info.progress.map((p) => (
                <div key={p.position} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded ${
                  p.is_me ? "bg-indigo-50 border border-indigo-200" : ""
                }`}>
                  {p.status === "signed" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={p.is_me ? "font-medium text-indigo-900" : "text-gray-700"}>
                    #{p.position} · {p.role === "tenant" ? "Locataire" : "Propriétaire"} — {p.name}
                  </span>
                  {p.status === "signed" && <span className="text-[10px] text-emerald-700 ml-auto">signé</span>}
                  {p.is_me && p.status === "pending" && <span className="text-[10px] text-indigo-700 ml-auto">vous</span>}
                </div>
              ))}
            </div>
          </div>

          {completed && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-900">
              <CheckCircle2 className="w-8 h-8 mb-2" />
              <h3 className="font-bold mb-1">Bail entièrement signé</h3>
              <p className="text-sm">Toutes les parties ont signé. Le PDF final est archivé chez votre comptable.</p>
            </div>
          )}

          {alreadySigned && !completed && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-900">
              <CheckCircle2 className="w-8 h-8 mb-2" />
              <h3 className="font-bold mb-1">Votre signature est enregistrée</h3>
              <p className="text-sm">Le workflow continue avec le prochain signataire. Vous recevrez la copie finale par courriel quand tout sera complété.</p>
            </div>
          )}

          {notMyTurn && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900">
              <Clock className="w-8 h-8 mb-2" />
              <h3 className="font-bold mb-1">Pas encore votre tour</h3>
              <p className="text-sm">Veuillez attendre que les signataires précédents signent. Vous recevrez un nouveau courriel quand ce sera votre tour.</p>
            </div>
          )}

          {info.is_my_turn && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold mb-2">Votre signature</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 mb-2">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                  className="w-full h-[150px] cursor-crosshair touch-none"
                  data-testid="bail-sign-canvas"
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={clearCanvas}
                  className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 inline-flex items-center gap-1"
                  data-testid="bail-sign-clear-btn"
                >
                  <Trash2 className="w-3 h-3" /> Effacer
                </button>
                <span className="text-[10px] text-gray-400">Dessinez votre signature</span>
              </div>
              <label className="flex items-start gap-2 mt-3 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 rounded text-indigo-600"
                  data-testid="bail-sign-accept-checkbox"
                />
                <span>
                  J&apos;accepte que ma signature électronique soit apposée sur le bail
                  et que sa valeur juridique soit équivalente à une signature manuscrite
                  (art. 2827 C.c.Q. et Loi concernant le cadre juridique des TI).
                </span>
              </label>
              <button
                onClick={submit}
                disabled={!accepted || submitting}
                className="w-full mt-3 px-4 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="bail-sign-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Envoi en cours…" : "Signer le bail"}
              </button>
            </div>
          )}
        </div>

        {/* Right : PDF preview */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="bail-pdf-preview">
          {pdfUrl && isMobileOrIOS() ? (
            // CR-001 — sur téléphone : ouverture via l'URL serveur (pas de blob: dans Safari iOS)
            <PdfFrame src={pdfUrl} openUrl={`${API}/public/bail-sign/${token}/pdf`} filename="bail-tal.pdf"
              title="Bail TAL" label="Ouvrir le bail (PDF)" accent="#4f46e5" testId="bail-pdf-frame" />
          ) : pdfUrl ? (
            // Batch #14.145 — <object> pour rendre les 8 pages avec contrôles natifs PDF.js
            <object
              data={pdfUrl}
              type="application/pdf"
              title="Bail TAL"
              className="w-full"
              style={{ height: "85vh", minHeight: 600 }}
            >
              <div className="p-8 text-center">
                <p className="text-sm text-gray-600 mb-2">Votre navigateur ne peut afficher le PDF intégré.</p>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
                  Ouvrir le PDF dans un nouvel onglet
                </a>
              </div>
            </object>
          ) : (
            <div className="p-12 text-center text-gray-400">Chargement du PDF…</div>
          )}
        </div>
      </div>
    </div>
  );
}
